import React, { useState, useEffect, useCallback } from "react";
import { tooltipStyleProps } from "services/constants/Styles";
import * as Mui from "@mui/material";
import { showToast } from "services/utils/Status";
import { customAPI } from "services/utils/CustomAPI";
import { useSelector } from "react-redux";
import { themes } from "services/constants";
import { useFetch } from "services/hooks/useFetch";
import {
  specialCharacterRestrict,
  atleastOneCharacterWithSpace,
} from "services/constants/patterns";
import { Close, Check, AddCircleOutline, Info } from "@mui/icons-material";
import { rowEndStyle } from "services/constants/Styles";
import {
  CustomAutoComplete,
  CustomButton,
  BackArrow,
} from "components/CustomComponents";
import { customGetAPI } from "services/utils/CustomAPI";
import { tableRowSx, tableCellFontSize } from "services/constants";
export const EditPermissionRole = ({
  setScreenNavigation,
  editRoleDetails,
}) => {
  const [productRolesDetails, setProductRolesDetails] = useState([]);
  const { domain } = useSelector((state) => state.tokenReducer);
  const { result: productsData, loading: productLoading } = useFetch(
    `${domain}product-control/role/?role_id=${editRoleDetails?.role_id}`
  );
  const { result: products, loading } = useFetch(
    `${domain}product-control/products/`
  );
  const [productData, setProductData] = useState(products);
  const [firstTimeTrigger, setFristTimeTrigger] = useState(true);
  const [currentSelectedProduct, setCurrentSelectedProduct] = useState("");
  const [productRoleName, setProductRoleName] = useState(
    editRoleDetails?.role_name
  );
  const [errorMessage, setErrorMessage] = useState("");
  const [showNewProducts, setShowNewProducts] = useState(false);
  const [tableHeight, setTableHeight] = useState(window.innerHeight * 0.7);
  const [newProduct, setNewProduct] = useState({
    product_name: "",
    product_id: "",
  });

  const removeAddedProduct = useCallback((products, addedProducts) => {
    let allProducts = products;
    addedProducts.forEach((all_data) => {
      allProducts = allProducts.filter((data) => {
        return data?.product_name !== all_data?.product_name;
      });
    });
    setProductData(allProducts);
    setShowNewProducts(false);
    setNewProduct((prev) => ({
      ...prev,
      product_name: "",
      product_id: "",
    }));
  }, []);

  useEffect(() => {
    const updateDataConfigurations = () => {
      setProductData(products);
      setProductRolesDetails(productsData?.products);
      removeAddedProduct(products, productsData?.products);
      setCurrentSelectedProduct(
        productsData?.products ? productsData?.products[0]?.product_name : ""
      );
    };
    if (!loading && firstTimeTrigger && !productLoading) {
      updateDataConfigurations();
      setFristTimeTrigger(false);
    }
  }, [
    firstTimeTrigger,
    loading,
    productLoading,
    products,
    productsData,
    removeAddedProduct,
  ]);

  useEffect(() => {
    const updateHeight = () => {
      setTableHeight(window.innerHeight * 0.7);
    };

    window.addEventListener("resize", updateHeight);

    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  const addNewProducts = () => {
    setShowNewProducts(true);
  };

  const handleNewProduct = (event, data) => {
    if (data) {
      setNewProduct((prev) => ({
        ...prev,
        product_name: data?.label,
        product_id: data?.product_id,
      }));
    }
  };

  const updateNewProducts = async () => {
    if (newProduct?.product_name) {
      const res = await customGetAPI(
        `${domain}product-control/products/summary/?product_id=${newProduct?.product_id}`
      );
      if (res?.code) {
        setProductRolesDetails([...productRolesDetails, res?.responseDetails]);
        removeAddedProduct(products, [
          ...productRolesDetails,
          res?.responseDetails,
        ]);
        setCurrentSelectedProduct(newProduct?.product_name);
      }
    }
  };

  const removeProduct = (data) => {
    let filteredValue = productRolesDetails.filter((filter_data) => {
      return filter_data?.product_name !== data;
    });
    setProductRolesDetails(filteredValue);
    removeAddedProduct(products, filteredValue);
    setShowNewProducts(false);
  };

  const updateCheckBoxDetails = (value, key, moduleName, sub_module_name) => {
    let updatedData = productRolesDetails.map((product) => {
      if (product.product_name !== currentSelectedProduct) return product;
      return {
        ...product,
        module_details: product.module_details.map((module) => {
          if (module.module_name !== moduleName) return module;

          return {
            ...module,
            submodules: module.submodules.map((submodule) => {
              if (submodule.sub_module_name !== sub_module_name)
                return submodule;

              let updatedPermissions = {
                ...submodule.permissions,
                [key]: value,
              };
              if (key === "second_level_approver" && value) {
                updatedPermissions.first_level_approver = true;
                updatedPermissions.editor = true;
                updatedPermissions.viewer = true;
              } else if (key === "first_level_approver" && value) {
                updatedPermissions.editor = true;
                updatedPermissions.viewer = true;
              } else if (key === "editor" && value) {
                updatedPermissions.viewer = true;
              }
              if (key === "editor" && !value) {
                updatedPermissions.first_level_approver = false;
                updatedPermissions.second_level_approver = false;
              } else if (key === "first_level_approver" && !value) {
                updatedPermissions.second_level_approver = false;
              } else if (key === "viewer" && !value) {
                updatedPermissions.editor = false;
                updatedPermissions.viewer = false;
                updatedPermissions.first_level_approver = false;
                updatedPermissions.second_level_approver = false;
              }
              return {
                ...submodule,
                permissions: updatedPermissions,
              };
            }),
          };
        }),
      };
    });
    setProductRolesDetails(updatedData);
  };

  const validateProductName = () => {
    if (!productRoleName.trim()) {
      setErrorMessage("Product name is required");
      return false;
    } else if (productRoleName.length < 3) {
      setErrorMessage("Minimum 3 characters required");
      return false;
    } else if (!atleastOneCharacterWithSpace.test(productRoleName)) {
      setErrorMessage("At least one character is required");
      return false;
    } else {
      setErrorMessage("");
      return true;
    }
  };

  const createNewRole = async () => {
    if (productRoleName === editRoleDetails?.role_name) {
      await editPermission();
    } else {
      const res = await customGetAPI(
        `${domain}unique/role/?name=${productRoleName.trim()}`
      );
      if (res.ok) {
        if (res?.responseDetails?.exists) {
          setErrorMessage("Product role name already exists");
        } else {
          setErrorMessage("");
          await editPermission();
        }
      }
    }
  };

  const editPermission = async () => {
    if (validateProductName()) {
      if (productRolesDetails.length < 1) {
        setErrorMessage("Minimum one product is required");
      } else {
        const response = await customAPI(
          `${domain}product-control/role/`,
          "PUT",
          {
            role_id: editRoleDetails?.role_id,
            role_name: productRoleName.trim(),
            products: productRolesDetails,
          }
        );
        if (response.ok) {
          showToast(
            "success",
            `The product permissions have been successfully updated.`
          );
          setScreenNavigation("");
          setProductRolesDetails([]);
        }
      }
    }
  };

  const checkNameAlreadyExists = async () => {
    const res = await customGetAPI(
      `${domain}unique/role/?name=${productRoleName.trim()}`
    );
    if (res.ok) {
      if (res?.responseDetails?.exists) {
        setErrorMessage("Product role name already exists");
        return false;
      } else {
        setErrorMessage("");
        return true;
      }
    }
  };

  return (
    <React.Fragment>
      <Mui.Grid container sx={{ paddingTop: 1 }}>
        <BackArrow
          actionFunction={() => {
            setScreenNavigation("");
            setProductRolesDetails([]);
          }}
        />
        &nbsp;
        <Mui.Typography
          sx={{
            fontSize: 22,
            paddingLeft: 1,
            fontWeight: "bold",
            paddingTop: 1,
          }}
        >
          Edit Product Role
        </Mui.Typography>
      </Mui.Grid>
      <Mui.Grid container sx={{ paddingTop: 0.5 }}>
        <Mui.Grid xs={2}></Mui.Grid>
        <Mui.Grid xs={6}>
          <Mui.Grid container>
            <Mui.Grid
              xs={6}
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "flex-end",
                mb: errorMessage !== "" ? 3 : 1,
              }}
            >
              <Mui.Typography
                sx={{
                  fontWeight: "bold",
                  fontSize: 20,
                  color: themes.headLine,
                }}
              >
                Product Role
              </Mui.Typography>
              &nbsp;
              <Mui.Tooltip
                arrow
                slotProps={tooltipStyleProps}
                title="Define the title or responsibility level for the user, which helps group permission settings under a common role."
              >
                <Info size="small" />
              </Mui.Tooltip>
            </Mui.Grid>

            <Mui.Grid xs={6} sx={{ paddingLeft: 1 }}>
              <Mui.TextField
                value={productRoleName}
                onChange={(event) => {
                  if (
                    specialCharacterRestrict.test(event.target.value) &&
                    event.target.value.length <= 100
                  ) {
                    setProductRoleName(event.target.value);
                  }
                }}
                sx={{ width: 500 }}
                size="small"
                placeholder="Enter the product role"
                onBlur={() => {
                  if (productRoleName !== editRoleDetails?.role_name) {
                    checkNameAlreadyExists();
                  }
                }}
                error={
                  [
                    "Product role name already exists",
                    "At least one character is required",
                    "Product name is required",
                    "Minimum 3 characters required",
                  ].includes(errorMessage)
                    ? true
                    : false
                }
              />
              {[
                "Product role name already exists",
                "At least one character is required",
                "Product name is required",
                "Minimum 3 characters required",
              ].includes(errorMessage) && (
                <Mui.Typography
                  variant="body2"
                  color="error"
                  sx={{ fontSize: 13, paddingLeft: 1 }}
                >
                  {errorMessage}
                </Mui.Typography>
              )}
            </Mui.Grid>
          </Mui.Grid>
        </Mui.Grid>
        <Mui.Grid xs={4}></Mui.Grid>
      </Mui.Grid>
      <Mui.Grid container sx={{ padding: 1 }}>
        <Mui.Grid
          xs={2.5}
          sx={{ minHeight: tableHeight }}
          component={Mui.Paper}
        >
          <Mui.Grid
            container
            sx={{ backgroundColor: themes.formBackground, padding: 1 }}
          >
            <Mui.Typography sx={{ fontWeight: "bold", fontSize: 18 }}>
              Products
            </Mui.Typography>
            &nbsp;
            <Mui.Tooltip
              arrow
              slotProps={tooltipStyleProps}
              title="Select the functional area of the system (e.g., TeamZone) for which you want to configure permissions."
            >
              <Info />
            </Mui.Tooltip>
          </Mui.Grid>
          {productRolesDetails?.map((data) => (
            <Mui.Box
              sx={{ paddingTop: 0.5, cursor: "pointer" }}
              onClick={() => {
                setCurrentSelectedProduct(data?.product_name);
              }}
            >
              <Mui.Grid
                container
                sx={{
                  padding: 1,
                  backgroundColor:
                    currentSelectedProduct === data?.product_name
                      ? themes.primaryIndicator
                      : "none",
                }}
              >
                <Mui.Grid xs={9}>
                  <Mui.Typography
                    sx={{
                      fontSize: 15,
                      color: themes.headLine,
                    }}
                  >
                    {data?.product_name}
                  </Mui.Typography>
                </Mui.Grid>
                <Mui.Grid xs={3}>
                  <Mui.IconButton
                    sx={{
                      backgroundColor: themes.lightRedColor,
                      p: 0.5,
                      "&:hover": {
                        backgroundColor: themes.lightRedColor,
                      },
                    }}
                    onClick={() => removeProduct(data?.product_name)}
                  >
                    <Close sx={{ color: themes.whiteColor, fontSize: 18 }} />
                  </Mui.IconButton>
                </Mui.Grid>
              </Mui.Grid>
            </Mui.Box>
          ))}
          {showNewProducts ? (
            <Mui.Grid container sx={{ padding: 1 }}>
              <Mui.Grid xs={9}>
                <CustomAutoComplete
                  dataList={productData?.map((obj) => ({
                    ...obj,
                    label: obj?.product_name,
                  }))}
                  selectedValue={newProduct?.product_name}
                  updateDataFunction={handleNewProduct}
                  placeHolder="Select the product"
                />
              </Mui.Grid>
              <Mui.Grid xs={3} sx={{ paddingTop: 1, paddingLeft: 1 }}>
                &nbsp;
                <Mui.IconButton
                  sx={{
                    backgroundColor: themes.greenColor,
                    p: 0.5,
                    "&:hover": {
                      backgroundColor: themes.greenColor,
                    },
                  }}
                  onClick={() => {
                    updateNewProducts();
                    setErrorMessage("");
                  }}
                >
                  <Check sx={{ color: themes.whiteColor, fontSize: 18 }} />
                </Mui.IconButton>{" "}
                &nbsp;
                <Mui.IconButton
                  sx={{
                    backgroundColor: themes.lightRedColor,
                    p: 0.5,
                    "&:hover": {
                      backgroundColor: themes.lightRedColor,
                    },
                  }}
                  onClick={() => {
                    setNewProduct((prev) => ({
                      ...prev,
                      product_name: "",
                      product_id: "",
                    }));
                    setShowNewProducts(false);
                  }}
                >
                  <Close sx={{ color: themes.whiteColor, fontSize: 18 }} />
                </Mui.IconButton>
              </Mui.Grid>
            </Mui.Grid>
          ) : null}
          {["Minimum one product is required"].includes(errorMessage) && (
            <Mui.Typography
              variant="body2"
              color="error"
              sx={{ fontSize: 17, paddingLeft: 1, paddingTop: 1 }}
            >
              {errorMessage}
            </Mui.Typography>
          )}

          <Mui.Grid container sx={{ ...rowEndStyle, padding: 1 }}>
            <Mui.IconButton
              onClick={() => {
                addNewProducts();
                if (errorMessage === "Minimum one product is required") {
                  setErrorMessage("");
                }
              }}
              disabled={showNewProducts || productData.length === 0}
            >
              <Mui.Tooltip
                arrow
                slotProps={tooltipStyleProps}
                title="Add Product"
              >
                <AddCircleOutline
                  sx={{
                    color:
                      showNewProducts || productData.length === 0
                        ? themes.formBackground
                        : themes.headLine,
                    stroke:
                      showNewProducts || productData.length === 0
                        ? themes.formBackground
                        : themes.headLine,
                    strokeWidth:
                      showNewProducts || productData.length === 0 ? 0 : 0.5,
                  }}
                />
              </Mui.Tooltip>
            </Mui.IconButton>
          </Mui.Grid>
        </Mui.Grid>
        <Mui.Grid xs={0.1}></Mui.Grid>
        <Mui.Grid
          xs={9.4}
          sx={{ minHeight: tableHeight }}
          component={Mui.Paper}
        >
          <Mui.Grid
            container
            sx={{ backgroundColor: themes.formBackground, padding: 1 }}
          >
            <Mui.Typography sx={{ fontWeight: "bold", fontSize: 18 }}>
              Permissions
            </Mui.Typography>
            &nbsp;
            <Mui.Tooltip
              arrow
              slotProps={tooltipStyleProps}
              title="Set specific access rights such as view,  edit or approve for the selected module based on the user role"
            >
              <Info />
            </Mui.Tooltip>
          </Mui.Grid>
          <Mui.Grid container>
            <Mui.TableContainer
              sx={{ mt: 1, mr: 1, ml: 1, width: "100%", maxHeight: 400 }}
            >
              <Mui.Table className="border">
                <Mui.TableHead
                  sx={{
                    backgroundColor: themes.primary,
                    position: "sticky",
                  }}
                >
                  <Mui.TableRow maxWidth="xl" align="left" sx={tableRowSx}>
                    <Mui.TableCell
                      align="center"
                      sx={{
                        color: themes.headLine,
                        fontWeight: "bold",
                        fontSize: tableCellFontSize,
                        width: "30%",
                      }}
                    >
                      Sub Modules
                    </Mui.TableCell>
                    <Mui.TableCell
                      align="center"
                      sx={{
                        color: themes.headLine,
                        fontWeight: "bold",
                        fontSize: tableCellFontSize,
                        width: "17.5%",
                      }}
                    >
                      View
                    </Mui.TableCell>
                    <Mui.TableCell
                      align="center"
                      sx={{
                        color: themes.headLine,
                        fontWeight: "bold",
                        fontSize: tableCellFontSize,
                        width: "17.5%",
                      }}
                    >
                      Edit
                    </Mui.TableCell>
                    <Mui.TableCell
                      align="center"
                      sx={{
                        color: themes.headLine,
                        fontWeight: "bold",
                        fontSize: tableCellFontSize,
                        width: "17.5%",
                      }}
                    >
                      Approver
                    </Mui.TableCell>
                    {/* <Mui.TableCell
                      align="center"
                      sx={{
                        color: themes.headLine,
                        fontWeight: "bold",
                        fontSize: tableCellFontSize,
                        width: "17.5%",
                      }}
                    >
                      II - Leave Approve
                    </Mui.TableCell> */}
                  </Mui.TableRow>
                </Mui.TableHead>
              </Mui.Table>
            </Mui.TableContainer>
          </Mui.Grid>
          {productRolesDetails?.map((data) => (
            <Mui.Box
              sx={{ cursor: "pointer" }}
              onClick={() => {
                setCurrentSelectedProduct(data?.product_name);
              }}
            >
              {currentSelectedProduct === data?.product_name ? (
                data?.module_details?.length === 0 ? (
                  <Mui.Grid container sx={{ padding: 1, width: "100%" }}>
                    <Mui.Alert severity="info" sx={{ width: "100%" }}>
                      No modules available
                    </Mui.Alert>
                  </Mui.Grid>
                ) : (
                  data?.module_details?.map((module) => (
                    <Mui.Grid container>
                      <Mui.TableContainer
                        sx={{
                          ml: 1,
                          mr: 1,
                          mb: 1,
                          width: "100%",
                          maxHeight: 400,
                        }}
                      >
                        <Mui.Table className="border">
                          <Mui.TableHead
                            sx={{
                              backgroundColor: themes.primary,
                              position: "sticky",
                              top: -1,
                              zIndex: 10,
                            }}
                          >
                            <Mui.TableRow
                              maxWidth="xl"
                              align="left"
                              sx={tableRowSx}
                            >
                              <Mui.TableCell
                                sx={{
                                  color: themes.headLine,
                                  fontWeight: "bold",
                                  fontSize: tableCellFontSize,
                                  width: "30%",
                                }}
                              >
                                {module?.module_name}
                              </Mui.TableCell>
                              <Mui.TableCell align="center"></Mui.TableCell>
                              <Mui.TableCell align="center"></Mui.TableCell>
                              <Mui.TableCell align="center"></Mui.TableCell>
                            </Mui.TableRow>
                          </Mui.TableHead>
                          <Mui.TableBody>
                            {module?.submodules.map((item, index) => (
                              <Mui.TableRow
                                maxWidth="xl"
                                align="left"
                                sx={{
                                  cursor: "pointer",
                                  "& .MuiTableCell-root": {
                                    height: "10px",
                                    padding: "3px 15px",
                                  },
                                }}
                              >
                                <Mui.TableCell
                                  align="center"
                                  sx={{
                                    color: themes.headLine,
                                    fontWeight: "bold",
                                    fontSize: tableCellFontSize,
                                    width: "30%",
                                  }}
                                >
                                  {item?.sub_module_name}
                                </Mui.TableCell>
                                <Mui.TableCell
                                  align="center"
                                  sx={{
                                    color: themes.headLine,
                                    fontWeight: "bold",
                                    fontSize: tableCellFontSize,
                                    width: "17.5%",
                                  }}
                                >
                                  <Mui.Checkbox
                                    checked={item?.permissions?.viewer}
                                    size="medium"
                                    readOnly={true}
                                  />
                                </Mui.TableCell>
                                <Mui.TableCell
                                  align="center"
                                  sx={{
                                    color: themes.headLine,
                                    fontWeight: "bold",
                                    fontSize: tableCellFontSize,
                                    width: "17.5%",
                                  }}
                                >
                                  <Mui.Checkbox
                                    checked={item?.permissions?.editor}
                                    onChange={(event) => {
                                      updateCheckBoxDetails(
                                        event.target.checked,
                                        "editor",
                                        module?.module_name,
                                        item?.sub_module_name
                                      );
                                    }}
                                    size="medium"
                                  />
                                </Mui.TableCell>
                                <Mui.TableCell
                                  align="center"
                                  sx={{
                                    color: themes.headLine,
                                    fontWeight: "bold",
                                    fontSize: tableCellFontSize,
                                    width: "17.5%",
                                  }}
                                >
                                  <Mui.Checkbox
                                    checked={
                                      item?.permissions?.first_level_approver
                                    }
                                    onChange={(event) => {
                                      updateCheckBoxDetails(
                                        event.target.checked,
                                        "first_level_approver",
                                        module?.module_name,
                                        item?.sub_module_name
                                      );
                                    }}
                                    size="medium"
                                  />
                                </Mui.TableCell>
                                {/* <Mui.TableCell
                                  align="center"
                                  sx={{
                                    color: themes.headLine,
                                    fontWeight: "bold",
                                    fontSize: tableCellFontSize,
                                    width: "17.5%",
                                  }}
                                >
                                  <Mui.Checkbox
                                    checked={
                                      item?.permissions?.second_level_approver
                                    }
                                    onChange={(event) => {
                                      updateCheckBoxDetails(
                                        event.target.checked,
                                        "second_level_approver",
                                        module?.module_name,
                                        item?.sub_module_name
                                      );
                                    }}
                                    size="medium"
                                  />
                                </Mui.TableCell> */}
                              </Mui.TableRow>
                            ))}
                          </Mui.TableBody>
                        </Mui.Table>
                      </Mui.TableContainer>
                    </Mui.Grid>
                  ))
                )
              ) : null}
            </Mui.Box>
          ))}
        </Mui.Grid>
      </Mui.Grid>
      <Mui.Grid
        container
        sx={{ ...rowEndStyle, paddingRight: 4, paddingTop: 2 }}
      >
        <CustomButton
          actionFuntion={() => {
            setScreenNavigation("");
            setProductRolesDetails([]);
          }}
          actionName="Cancel"
          typeName="button"
        />{" "}
        &nbsp;
        <CustomButton
          actionFuntion={() => {
            createNewRole();
          }}
          actionName="Update"
          typeName="button"
        />
      </Mui.Grid>
    </React.Fragment>
  );
};
